import React, { useMemo } from "react";
import { TextField, InputAdornment, Typography } from "@mui/material";
import MaterialReactTable, { MRT_ColumnDef } from "material-react-table";
import { Controller, type Control, type FieldPath } from "react-hook-form";
import type { TargetFormData, TargetType } from "../types";
import { TARGET_TYPES } from "../types";
import { customTableStyles } from "../styles";

export interface CustomAnnualTableProps {
  yearsBetween: number[];
  control: Control<TargetFormData>;
  targetType: TargetType;
}

const CUSTOM_ANNUAL_UNIT: Record<TargetType, string> = {
  [TARGET_TYPES.PERCENT_REDUCTION]: "%",
  [TARGET_TYPES.ABSOLUTE_TCO2E]: "tCO₂e",
};

export const CustomAnnualTable = ({ yearsBetween, control, targetType }: CustomAnnualTableProps) => {
  const data = useMemo(() => yearsBetween.map((year) => ({ year })), [yearsBetween]);
  const unit = CUSTOM_ANNUAL_UNIT[targetType] ?? "";

  const columns = useMemo<MRT_ColumnDef<{ year: number }>[]>(
    () => [
      {
        accessorKey: "year",
        header: "Year",
        size: 80,
      },
      {
        id: "value",
        header: "Value",
        size: 120,
        Cell: ({ row }) => (
          <Controller
            name={`customAnnualValues.${String(row.original.year)}` as FieldPath<TargetFormData>}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                type="number"
                fullWidth
                variant="outlined"
                value={field.value ?? ""}
                onChange={(e) => field.onChange(e.target.value)}
                sx={{ "& .MuiInputBase-root": { fontSize: "14px" } }}
                InputProps={{
                  endAdornment: unit ? (
                    <InputAdornment position="end">
                      <Typography variant="body2" color="text.secondary">
                        {unit}
                      </Typography>
                    </InputAdornment>
                  ) : undefined,
                }}
                // eslint-disable-next-line react/jsx-no-duplicate-props -- InputProps (MUI) and inputProps (native input) are different
                inputProps={{
                  style: {
                    paddingRight: unit ? (unit === "%" ? 36 : 56) : 8,
                    paddingLeft: 8,
                    paddingTop: 6,
                    paddingBottom: 6,
                  },
                }}
              />
            )}
          />
        ),
      },
    ],
    [control, unit]
  );

  return (
    <MaterialReactTable
      columns={columns}
      data={data}
      enablePagination={false}
      enableTopToolbar={false}
      enableBottomToolbar={false}
      enableColumnActions={false}
      enableColumnFilters={false}
      enableSorting={false}
      enableStickyHeader
      {...customTableStyles}
    />
  );
};
